import { useEffect, useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import { useRidesActions, useRidesNavigation } from "@ryde/redux";
import {
  IRideDto,
  IRidesCalendarEventDto,
  tableRowsAtomFamily,
} from "@ryde/shared";

import { RIDES_CALENDAR_ENTITY } from "./parts/rides-calendar.constants";
import { mapRideToCalendarEvent } from "./rides-calendar.helpers";

export const useRidesCalendarEvents = (): {
  events: IRidesCalendarEventDto[];
  truncated: boolean;
  refetch: () => Promise<void>;
} => {
  const tableRows = useRecoilValue(tableRowsAtomFamily(RIDES_CALENDAR_ENTITY));
  const { isRidesEditOrAddRoute } = useRidesNavigation();
  const { fetchRides } = useRidesActions();
  const prevIsEditOrAddRouteRef = useRef(isRidesEditOrAddRoute);

  const events = useMemo(
    () =>
      ((tableRows ?? []) as IRideDto[])
        .filter(ride => Boolean(ride?.id) && Boolean(ride.rideStart))
        .map(mapRideToCalendarEvent),
    [tableRows],
  );

  const refetch = async (): Promise<void> => {
    await fetchRides({ skipPageReset: true, isBypassPreloader: true });
  };

  useEffect(() => {
    const wasOnEditOrAddRoute = prevIsEditOrAddRouteRef.current;
    prevIsEditOrAddRouteRef.current = isRidesEditOrAddRoute;
    if (wasOnEditOrAddRoute && !isRidesEditOrAddRoute) {
      void fetchRides({ skipPageReset: true, isBypassPreloader: true });
    }
  }, [isRidesEditOrAddRoute, fetchRides]);

  return { events, truncated: false, refetch };
};
